import { useMemo } from "react";
import { useDynamicConfig } from "@statsig/react-bindings";

import { useGlobalContext } from "@/hooks/useGlobalContext";
import { SofaClientRequestError } from "@/lib/sofaClient";
import { SofaApiError } from "@/lib/sofaErrors";

import {
  BAZAAR_CURRENCY_DEFAULTS_CONFIG,
  currencyAmountToMicros,
  getCurrencyFractionDigits,
  getZeroDecimalCurrencyCodes,
  MICROS_PER_CURRENCY_UNIT,
  normalizeCurrencyCode,
  ZERO_DECIMAL_CURRENCY_CODES_CONFIG_KEY,
} from "./CurrencyDefaults";
import { useAccountTimezone } from "./useAccountTimezone";

import type { DateTime } from "luxon";
import type { Option } from "@oai/platform/ui/Select";
import type { AdManagerEntityType } from "./AdManagerQueries";
import type { ZeroDecimalCurrencyCodes } from "./CurrencyDefaults";

/**
 * ISO 4217 currency code for an ad account.
 */
export type ISO4217CurrencyCode = string;

/**
 * IANA Timezone identifier.
 */
export type IANATimeZone = string;

// TODO: Consider asserting that ad account currency is loaded instead of falling back to USD.
const FALLBACK_ACCOUNT_CURRENCY = toISO4217CurrencyCode("USD");
export const ONE_MILLION = MICROS_PER_CURRENCY_UNIT;
export const AD_MANAGER_ENTITY_NAME_MIN_LENGTH = 3;
export const AD_MANAGER_API_URL_MAX_LENGTH = 2_048;
export const MS_IN_SEC = 1_000;
export const SEC_IN_MIN = 60;
export const MIN_IN_HR = 60;
export const MS_IN_HR = MS_IN_SEC * SEC_IN_MIN * MIN_IN_HR;
export const MS_IN_DAY = MS_IN_HR * 24;

const AD_MANAGER_ENTITY_TYPE_BY_PATH: Partial<Record<string, AdManagerEntityType>> = {
  "/manage/campaigns": "campaign",
  "/manage/adgroups": "ad_group",
  "/manage/ads": "ad",
};

// Always use "en-US" locale for numeric/currency formatting to ensure consistent separators ("," and ".")
const NUMERIC_LOCALE = "en-US";
const AD_GROUP_MAX_BID_MICROS_BY_CURRENCY_CONFIG_KEY = "ad_group_max_bid_micros_by_currency";
const DEFAULT_AD_GROUP_MAX_BID_MICROS_BY_CURRENCY: Record<string, number> = {
  USD: 60_000,
  AUD: 84_000,
  NZD: 101_500,
  CAD: 82_500,
  GBP: 45_000,
  KRW: 91_000_000,
  JPY: 10_000_000,
  BRL: 340_000,
  MXN: 1_200_000,
};

export function getDefaultAdGroupMaxBidMicrosByCurrency(
  value: unknown,
  fallback: Record<string, number> = DEFAULT_AD_GROUP_MAX_BID_MICROS_BY_CURRENCY,
): Record<string, number> {
  if (typeof value !== "object" || value == null || Array.isArray(value)) {
    return { ...fallback };
  }

  return Object.entries(value as Record<string, unknown>).reduce<Record<string, number>>(
    (defaultsByCurrency, [currency, amount]) => {
      const normalizedCurrency = normalizeCurrencyCode(currency);
      const parsedAmount =
        typeof amount === "number" ? amount
        : typeof amount === "string" ? Number(amount.trim())
        : NaN;
      if (!normalizedCurrency || !Number.isSafeInteger(parsedAmount) || parsedAmount <= 0) {
        return defaultsByCurrency;
      }
      return { ...defaultsByCurrency, [normalizedCurrency]: parsedAmount };
    },
    { ...fallback },
  );
}

export function getAdManagerEntityTypeFromPath(pathname: string): AdManagerEntityType | undefined {
  return AD_MANAGER_ENTITY_TYPE_BY_PATH[pathname];
}

export function getEntityTypeFromPath(pathname: string): AdManagerEntityType {
  return getAdManagerEntityTypeFromPath(pathname) ?? "ad";
}

const ARCHIVED_PRODUCT_FEED_ERROR_MESSAGES = new Set([
  "This campaign uses an archived product feed and cannot be activated. Create a new campaign using an active product feed.",
  "This campaign uses an archived product feed. Create a new campaign using an active product feed.",
  "This product feed has been archived. Choose an active product feed or create and upload a new one.",
]);

export function isAdsManagerArchivedProductFeedMessage(
  message: unknown,
  expectedStatus?: number,
): boolean {
  if (typeof message !== "string") {
    return false;
  }

  const normalizedMessage = message.trim();
  const statusPrefix = /^(400|409): /u.exec(normalizedMessage);
  if (statusPrefix && expectedStatus !== undefined && statusPrefix[1] !== String(expectedStatus)) {
    return false;
  }

  return ARCHIVED_PRODUCT_FEED_ERROR_MESSAGES.has(
    statusPrefix ?
      normalizedMessage.slice(statusPrefix[0].length)
    : normalizedMessage.replace(/^ProductFeedOwnershipError: /u, ""),
  );
}

export function isAdsManagerArchivedProductFeedError(error: unknown): boolean {
  if (!(error instanceof SofaApiError) || (error.status !== 400 && error.status !== 409)) {
    return false;
  }

  return (
    (error.status === 409 && error.code === "product_feed_archived") ||
    isAdsManagerArchivedProductFeedMessage(error.message, error.status)
  );
}

export function getAdsManagerSafeErrorMessage(error: unknown, fallback: string): string {
  if (isAdsManagerUnsafeError(error)) {
    return fallback;
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
}

function isAdsManagerUnsafeError(error: unknown): boolean {
  if (error instanceof SofaApiError || error instanceof SofaClientRequestError) {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  if (/^API service .* returned HTTP \d+$/.test(error.message)) {
    return true;
  }

  return error.cause instanceof SofaApiError || error.cause instanceof SofaClientRequestError;
}

export type AccountFormatters = {
  accountCurrency: ISO4217CurrencyCode;
  accountTimezone: IANATimeZone;
  shortTimezoneLabel: string;
  accountCurrencySymbol: string;
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes;
  requiresWholeCurrencyUnits: boolean;
  defaultAdGroupMaxBidMicros: number | null;
  // Formatter for raw numeric inputs
  formatMicrosForInput: (amountMicros: number) => string;
  // Format number for read-only display (with thousands separator commas)
  formatMicrosForDisplay: (amountMicros: number) => string;
  // Format normal currency units (not micros) for read-only display.
  formatAmountForDisplay: (amount: number) => string;
  // Parses user-entered amount text into micros using account-currency separators.
  getCurrencyInMicros: (amount: string | number) => number | null;
  formatDate: (timestampInSeconds: number) => string;
};

export function formatMicrosForInputValue(
  amountMicros: number,
  { maximumFractionDigits = 2 }: { maximumFractionDigits?: number } = {},
): string {
  return getAmountFormatter({ maximumFractionDigits }).format(amountMicros / ONE_MILLION);
}

export function getRowInsightsSelectionUpdate(
  selectedIds: string[],
  rowId: string,
): Set<string> | null {
  return selectedIds.includes(rowId) ? null : new Set([rowId]);
}

export function getDateRangeDayCount([start, end]: readonly [DateTime, DateTime]): number {
  return Math.max(1, Math.floor(end.diff(start, "days").days) + 1);
}

export type AdInsightSummary = {
  impressions: string;
  clicks: string;
  spend: string;
  ctr: string;
  cpc: string;
  cpm: string;
};

const PERCENTAGE_FORMATTER = new Intl.NumberFormat(NUMERIC_LOCALE, {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function buildAccountFormatters({
  accountCurrency,
  accountTimezone,
  defaultAdGroupMaxBidMicrosByCurrency,
  zeroDecimalCurrencyCodes,
  locale = "en-US",
}: {
  accountCurrency: ISO4217CurrencyCode;
  accountTimezone: IANATimeZone;
  defaultAdGroupMaxBidMicrosByCurrency: Record<string, number>;
  zeroDecimalCurrencyCodes: ZeroDecimalCurrencyCodes;
  locale?: string;
}): AccountFormatters {
  const resolvedCurrencySymbol = getCurrencySymbol(accountCurrency);
  const fractionDigits = getCurrencyFractionDigits(accountCurrency, zeroDecimalCurrencyCodes);
  const requiresWholeCurrencyUnits = fractionDigits === 0;
  // Grouped formatter is for read-only monetary display.
  const inputAmountFormatter = getAmountFormatter({
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  });
  const legacyFractionalInputAmountFormatter = getAmountFormatter({
    maximumFractionDigits: 6,
    minimumFractionDigits: 0,
  });
  const displayAmountFormatter = getAmountFormatter({
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
    useGrouping: true,
  });
  const dateFormatter = getDateFormatter(locale, accountTimezone);

  return {
    accountCurrency: accountCurrency,
    accountTimezone: accountTimezone,
    shortTimezoneLabel: getShortTimeZoneName(accountTimezone),
    accountCurrencySymbol: resolvedCurrencySymbol,
    zeroDecimalCurrencyCodes,
    requiresWholeCurrencyUnits,
    formatMicrosForInput: (amountMicros: number) => {
      const amount = amountMicros / ONE_MILLION;
      if (requiresWholeCurrencyUnits && Math.abs(amountMicros) % ONE_MILLION !== 0) {
        return legacyFractionalInputAmountFormatter.format(amount);
      }
      return inputAmountFormatter.format(amount);
    },
    formatMicrosForDisplay: (amountMicros: number) =>
      displayAmountFormatter.format(amountMicros / ONE_MILLION),
    formatAmountForDisplay: (amount: number) => displayAmountFormatter.format(amount),
    formatDate: (timestampInSeconds: number) =>
      dateFormatter.format(new Date(timestampInSeconds * MS_IN_SEC)),
    defaultAdGroupMaxBidMicros: getDefaultAdGroupMaxBidMicrosForCurrency(
      accountCurrency,
      defaultAdGroupMaxBidMicrosByCurrency,
    ),
    getCurrencyInMicros: (amount: string | number) => {
      const parsedAmount = parseCurrencyAmount(amount);
      if (parsedAmount == null) {
        return null;
      }
      return currencyAmountToMicros({
        amount: parsedAmount,
        currency: accountCurrency,
        zeroDecimalCurrencyCodes,
      });
    },
  };
}

export function getDefaultAdGroupMaxBidMicrosForCurrency(
  currencyCode: string | null | undefined,
  defaultAdGroupMaxBidMicrosByCurrency: Record<
    string,
    number
  > = DEFAULT_AD_GROUP_MAX_BID_MICROS_BY_CURRENCY,
): number | null {
  const normalizedCurrency = normalizeCurrencyCode(currencyCode);
  if (!normalizedCurrency) {
    return null;
  }
  return defaultAdGroupMaxBidMicrosByCurrency[normalizedCurrency] ?? null;
}

/**
 * @returns formatter helper functions based on account settings like timezone and currency
 */
export function useAccountFormatters(options?: { locale?: string }): AccountFormatters {
  const { adAccountCurrencyCode } = useGlobalContext();
  const currencyDefaultsConfig = useDynamicConfig(BAZAAR_CURRENCY_DEFAULTS_CONFIG);
  const accountCurrency =
    adAccountCurrencyCode != null ?
      toISO4217CurrencyCode(adAccountCurrencyCode)
    : FALLBACK_ACCOUNT_CURRENCY;
  const accountTimezone = useAccountTimezone();
  const zeroDecimalCurrencyCodes = useMemo(
    () =>
      getZeroDecimalCurrencyCodes(
        currencyDefaultsConfig.get(ZERO_DECIMAL_CURRENCY_CODES_CONFIG_KEY, null),
      ),
    [currencyDefaultsConfig],
  );
  const defaultAdGroupMaxBidMicrosByCurrency = useMemo(
    () =>
      getDefaultAdGroupMaxBidMicrosByCurrency(
        currencyDefaultsConfig.get(AD_GROUP_MAX_BID_MICROS_BY_CURRENCY_CONFIG_KEY, null),
      ),
    [currencyDefaultsConfig],
  );

  return useMemo(
    () =>
      buildAccountFormatters({
        accountCurrency,
        accountTimezone,
        defaultAdGroupMaxBidMicrosByCurrency,
        zeroDecimalCurrencyCodes,
        locale: options?.locale,
      }),
    [
      accountCurrency,
      accountTimezone,
      defaultAdGroupMaxBidMicrosByCurrency,
      options?.locale,
      zeroDecimalCurrencyCodes,
    ],
  );
}

function toISO4217CurrencyCode(currency: string): ISO4217CurrencyCode {
  return currency as ISO4217CurrencyCode;
}

function getAmountFormatter({
  maximumFractionDigits = 2,
  minimumFractionDigits = 2,
  useGrouping = false,
}: {
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  useGrouping?: boolean;
} = {}): Intl.NumberFormat {
  return new Intl.NumberFormat(NUMERIC_LOCALE, {
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping,
  });
}

export function formatCurrencyAmountForDisplay({
  amount,
  accountCurrencySymbol,
  formatAmountForDisplay,
}: {
  amount: number;
  accountCurrencySymbol: string;
  formatAmountForDisplay: (amount: number) => string;
}): string {
  return `${accountCurrencySymbol}${formatAmountForDisplay(amount)}`;
}

export function formatPercentageForDisplay(value: number): string {
  return PERCENTAGE_FORMATTER.format(value);
}

function getDateFormatter(locale: string, timeZone: IANATimeZone): Intl.DateTimeFormat {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZone: resolveSupportedTimeZone(timeZone),
  };

  return new Intl.DateTimeFormat(locale, options);
}

export function parseAdManagerDateFilterInput(
  value: string,
  locale = "en-US",
): TimeZoneDateParts | null {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  const isoMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmedValue);
  if (isoMatch) {
    return getValidDateParts({
      year: Number(isoMatch[1]),
      month: Number(isoMatch[2]),
      day: Number(isoMatch[3]),
    });
  }

  const localeDateMatch = /^(\d{1,4})[\s./-]+(\d{1,4})[\s./-]+(\d{1,4})$/.exec(trimmedValue);
  if (localeDateMatch == null) {
    return null;
  }
  const dateParts = localeDateMatch.slice(1);

  const datePartOrder = getDateFilterInputPartOrder(locale);
  const parsedParts: Partial<Record<"year" | "month" | "day", number>> = {};
  datePartOrder.forEach((partName, index) => {
    const rawPart = dateParts[index] ?? "";
    parsedParts[partName] =
      partName === "year" && rawPart.length === 2 ? 2000 + Number(rawPart) : Number(rawPart);
  });

  return getValidDateParts({
    year: parsedParts.year ?? 0,
    month: parsedParts.month ?? 0,
    day: parsedParts.day ?? 0,
  });
}

function getDateFilterInputPartOrder(locale: string): Array<"year" | "month" | "day"> {
  const parts = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(Date.UTC(2026, 4, 5, 12, 0, 0, 0)));
  const partOrder = parts
    .map((part) => part.type)
    .filter(
      (part): part is "year" | "month" | "day" =>
        part === "year" || part === "month" || part === "day",
    );
  return partOrder.length === 3 ? partOrder : ["month", "day", "year"];
}

function getValidDateParts(dateParts: TimeZoneDateParts): TimeZoneDateParts | null {
  const date = new Date(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day, 12, 0, 0, 0));
  if (
    date.getUTCFullYear() !== dateParts.year ||
    date.getUTCMonth() !== dateParts.month - 1 ||
    date.getUTCDate() !== dateParts.day
  ) {
    return null;
  }
  return dateParts;
}

/**
 * Parses a user-entered currency amount string into a number, ignoring any non-numeric prefixes (like "$")
 * and allowing commas as thousand separators.
 *
 * @example parseCurrencyAmount("$1,234.56") => 1234.56
 */
type ParsedCurrencyInput = {
  amount: number;
  normalizedText: string | null;
};

function parseCurrencyInput(amount: string | number): ParsedCurrencyInput | null {
  if (typeof amount === "number") {
    return Number.isFinite(amount) && amount >= 0 ? { amount, normalizedText: null } : null;
  }

  const trimmedAmount = amount.trim();

  // Allow non-numeric prefixes (such as "$" or other currency symbols)
  const pattern = /^[^\d.-]*([\d.,-]+)[^\d.-]*$/;
  const match = trimmedAmount.match(pattern);
  if (!match) {
    return null;
  }

  // Strip commas from the numeric portion
  const normalized = match[1].replace(/,/g, "");

  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 ?
      { amount: parsed, normalizedText: normalized }
    : null;
}

function parseCurrencyAmount(amount: string | number): number | null {
  return parseCurrencyInput(amount)?.amount ?? null;
}

export type ParsedCurrencyAmount = {
  amountMicros: number;
  fractionDigits: number;
};

export function parseCurrencyAmountForValidation(amount: string): ParsedCurrencyAmount | null {
  const parsedInput = parseCurrencyInput(amount);
  if (parsedInput?.normalizedText == null) {
    return null;
  }

  const fractionDigits = (parsedInput.normalizedText.split(".")[1] ?? "").replace(/0+$/, "").length;
  return {
    amountMicros: Math.round(parsedInput.amount * ONE_MILLION),
    fractionDigits,
  };
}

/**
 * Returns the currency symbol if one is available, falling back to the currency code
 *
 * @example getCurrencySymbol("USD") => "$"
 * @example getCurrencySymbol("FJD") => "FJD"
 */
function getCurrencySymbol(currency: ISO4217CurrencyCode): string {
  try {
    const parts = new Intl.NumberFormat(NUMERIC_LOCALE, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).formatToParts(0);
    return parts.find((part) => part.type === "currency")?.value ?? currency;
  } catch {
    return currency;
  }
}

export function alignToCurrentUtcHourMs(ms: number): number {
  return Math.floor(ms / MS_IN_HR) * MS_IN_HR;
}

export function roundToMidnightUtcMs(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0);
}

type TimeZoneDateParts = {
  year: number;
  month: number;
  day: number;
};

type TimeZoneDateTimeParts = TimeZoneDateParts & {
  hour: number;
  minute: number;
  second: number;
};

export function getLocalMidnightUnixSecondsForDate(
  date: Date,
  timezone: IANATimeZone,
  dayOffset = 0,
): number {
  return getLocalMidnightUnixSecondsForDateParts(
    {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    },
    timezone,
    dayOffset,
  );
}

export function getLocalMidnightUnixSecondsForDateParts(
  dateParts: TimeZoneDateParts,
  timezone: IANATimeZone,
  dayOffset = 0,
): number {
  const adjustedDateParts = addDaysToDateParts(dateParts, dayOffset);
  return Math.floor(
    buildDateForTimeZone({
      ...adjustedDateParts,
      hour: 0,
      minute: 0,
      timezone,
    }).getTime() / MS_IN_SEC,
  );
}

export function getNextLocalMidnightUnixSeconds(timezone: IANATimeZone, now = new Date()): number {
  const currentLocalParts = getDateTimePartsForZone(now, timezone);
  const nextLocalDay = addDaysToDateParts(currentLocalParts, 1);
  return Math.floor(
    buildDateForTimeZone({
      ...nextLocalDay,
      hour: 0,
      minute: 0,
      timezone,
    }).getTime() / MS_IN_SEC,
  );
}

function addDaysToDateParts(dateParts: TimeZoneDateParts, dayOffset: number): TimeZoneDateParts {
  const adjusted = new Date(
    Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day + dayOffset, 12, 0, 0, 0),
  );
  return {
    year: adjusted.getUTCFullYear(),
    month: adjusted.getUTCMonth() + 1,
    day: adjusted.getUTCDate(),
  };
}

function buildDateForTimeZone({
  year,
  month,
  day,
  hour,
  minute,
  timezone,
}: TimeZoneDateParts & {
  hour: number;
  minute: number;
  timezone: IANATimeZone;
}): Date {
  const resolvedTimezone = resolveSupportedTimeZone(timezone);
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  const firstOffset = getTimeZoneOffsetMs(resolvedTimezone, utcGuess);
  const withFirstOffset = utcGuess - firstOffset;
  const secondOffset = getTimeZoneOffsetMs(resolvedTimezone, withFirstOffset);
  return new Date(utcGuess - secondOffset);
}

function getTimeZoneOffsetMs(timezone: IANATimeZone, timestampMs: number): number {
  const zonedDateTime = getDateTimePartsForZone(new Date(timestampMs), timezone);
  const asUtc = Date.UTC(
    zonedDateTime.year,
    zonedDateTime.month - 1,
    zonedDateTime.day,
    zonedDateTime.hour,
    zonedDateTime.minute,
    zonedDateTime.second,
    0,
  );
  return asUtc - timestampMs;
}

export function isSupportedTimeZone(timezone: IANATimeZone): boolean {
  try {
    createTimeZoneDateTimeFormatter(timezone);
    return true;
  } catch {
    return false;
  }
}

function getDateTimePartsForZone(date: Date, timezone: IANATimeZone): TimeZoneDateTimeParts {
  const formatter = createTimeZoneDateTimeFormatter(resolveSupportedTimeZone(timezone));
  const parts = formatter.formatToParts(date);
  let year = 0;
  let month = 0;
  let day = 0;
  let hour = 0;
  let minute = 0;
  let second = 0;

  for (const part of parts) {
    if (part.type === "year") {
      year = Number(part.value);
    } else if (part.type === "month") {
      month = Number(part.value);
    } else if (part.type === "day") {
      day = Number(part.value);
    } else if (part.type === "hour") {
      hour = Number(part.value);
    } else if (part.type === "minute") {
      minute = Number(part.value);
    } else if (part.type === "second") {
      second = Number(part.value);
    }
  }

  return { year, month, day, hour: hour % 24, minute, second };
}

function createTimeZoneDateTimeFormatter(timezone: IANATimeZone): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
}

function resolveSupportedTimeZone(timezone: IANATimeZone): IANATimeZone {
  return isSupportedTimeZone(timezone) ? timezone : "UTC";
}

/**
 * Returns the short timezone name for a given IANA timezone, accounting for daylight saving time if applicable.
 *
 * This should generally not be used directly - prefer getShortTimeZoneName
 *
 * @example getSeasonalShortTimeZoneName("America/Los_Angeles") => "PST" or "PDT" depending on the time of year
 * @example getSeasonalShortTimeZoneName("UTC") => "UTC"
 */
function getSeasonalShortTimeZoneName(timeZone: IANATimeZone): string {
  const resolvedTimeZone = resolveSupportedTimeZone(timeZone);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZoneName: "short",
    timeZone: resolvedTimeZone,
  });
  const parts = formatter.formatToParts(new Date());
  return parts.find((part) => part.type === "timeZoneName")?.value ?? resolvedTimeZone;
}

/**
 * Returns a more consistent short timezone label for display based on the account's IANA timezone, but
 * may fall back to the current seasonal abbreviation. Use this as a short label for the account timezone in the UI.
 *
 * @example getShortTimeZoneName("America/Los_Angeles") => "PST" (even during daylight saving time)
 * @example getShortTimeZoneName("America/New_York") => could be "EDT" or "EST" depending on the time of year, if it's not in our alias list
 */
function getShortTimeZoneName(timeZone: IANATimeZone): string {
  return TIMEZONE_ALIASES[timeZone] ?? getSeasonalShortTimeZoneName(timeZone) ?? timeZone;
}

const TIMEZONE_ALIASES: Record<string, string> = {
  "America/Los_Angeles": "PST",
  "UTC": "UTC",
  "GMT": "UTC",
};

const STABLE_TIME_ZONE_LABEL_DATE = new Date(Date.UTC(2021, 0, 1, 12));

const FALLBACK_TIMEZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Phoenix",
  "America/St_Johns",
  "America/Halifax",
  "America/Toronto",
  "America/Mexico_City",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Stockholm",
  "Europe/Warsaw",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Sydney",
  "Pacific/Auckland",
  "UTC",
] as const;

type IntlWithSupportedValues = typeof Intl & {
  supportedValuesOf?: (key: string) => string[];
};

type TimezoneOptionsConfig = {
  prioritizeBrowserTimezone?: boolean;
  browserTimeZoneSuffix?: string;
  detectedTimezone?: string | null;
  supportedTimeZoneIds?: readonly string[];
};

export const TIMEZONE_OPTIONS: Option<string>[] = getTimezoneOptions();

export function getTimezoneOptions(
  locale?: string,
  config: TimezoneOptionsConfig = {},
): Option<string>[] {
  const detectedTimezone =
    config.prioritizeBrowserTimezone === true ?
      normalizeSupportedTimeZoneId(config.detectedTimezone ?? getBrowserTimeZone())
    : null;

  return getOrderedTimeZoneIds({
    detectedTimezone,
    locale,
    timeZoneIds: config.supportedTimeZoneIds ?? getSupportedTimeZoneIds(),
  }).map((timezone) =>
    buildTimezoneOption(timezone, locale, {
      suffix:
        detectedTimezone === timezone && config.browserTimeZoneSuffix ?
          config.browserTimeZoneSuffix
        : undefined,
    }),
  );
}

export function buildTimezoneOption(
  timezone: string,
  locale?: string,
  options: { suffix?: string } = {},
): Option<string> {
  const label = getTimezoneLabel(timezone, locale);
  return {
    value: timezone,
    label: options.suffix ? `${label} (${options.suffix})` : label,
  };
}

export function getTimezoneLabel(timezone: string, locale?: string): string {
  const normalizedTimezone = timezone.trim();
  if (normalizedTimezone === "UTC" || normalizedTimezone === "GMT") {
    return "UTC";
  }

  const cityLabel = getTimeZoneCityLabel(normalizedTimezone);
  const genericTimeZoneName = getGenericTimeZoneName(normalizedTimezone, locale);
  const offsetLabel = getUtcOffsetLabel(normalizedTimezone);

  if (
    !genericTimeZoneName ||
    genericTimeZoneName.startsWith("GMT") ||
    genericTimeZoneName.startsWith("UTC")
  ) {
    return `${cityLabel} (${[normalizedTimezone, offsetLabel].filter(Boolean).join(", ")})`;
  }

  return `${cityLabel} - ${genericTimeZoneName}${offsetLabel ? ` (${offsetLabel})` : ""}`;
}

function getSupportedTimeZoneIds(): string[] {
  const intlWithSupportedValues = Intl as IntlWithSupportedValues;

  try {
    const timeZoneIds = intlWithSupportedValues.supportedValuesOf?.("timeZone");
    if (timeZoneIds != null && timeZoneIds.length > 0) {
      return includeFallbackTimeZones(timeZoneIds);
    }
  } catch {
    // Fall back to the historical curated list if the runtime cannot enumerate IANA zones.
  }

  return [...FALLBACK_TIMEZONES];
}

function getOrderedTimeZoneIds({
  detectedTimezone,
  locale,
  timeZoneIds,
}: {
  detectedTimezone: string | null;
  locale?: string;
  timeZoneIds: readonly string[];
}): string[] {
  const remainingTimezones = new Set(getUniqueSupportedTimeZoneIds(timeZoneIds));
  const orderedTimezones: string[] = [];

  if (detectedTimezone != null) {
    remainingTimezones.add(detectedTimezone);
    if (remainingTimezones.delete(detectedTimezone)) {
      orderedTimezones.push(detectedTimezone);
    }
  }

  for (const timezone of FALLBACK_TIMEZONES) {
    if (remainingTimezones.delete(timezone)) {
      orderedTimezones.push(timezone);
    }
  }

  const collator = new Intl.Collator(locale, { sensitivity: "base" });
  const sortedRemainingTimezones = [...remainingTimezones]
    .map((timezone) => ({
      timezone,
      sortLabel: getTimezoneSortLabel(timezone, locale),
    }))
    .sort(
      (firstTimezone, secondTimezone) =>
        collator.compare(firstTimezone.sortLabel, secondTimezone.sortLabel) ||
        collator.compare(firstTimezone.timezone, secondTimezone.timezone),
    )
    .map(({ timezone }) => timezone);

  return [...orderedTimezones, ...sortedRemainingTimezones];
}

function getUniqueSupportedTimeZoneIds(timeZoneIds: readonly string[]): string[] {
  const uniqueTimeZoneIds: string[] = [];
  const seenTimeZoneIds = new Set<string>();

  for (const timeZoneId of timeZoneIds) {
    const normalizedTimeZoneId = normalizeSupportedTimeZoneId(timeZoneId);
    if (normalizedTimeZoneId == null || seenTimeZoneIds.has(normalizedTimeZoneId)) {
      continue;
    }

    seenTimeZoneIds.add(normalizedTimeZoneId);
    uniqueTimeZoneIds.push(normalizedTimeZoneId);
  }

  return uniqueTimeZoneIds;
}

function normalizeSupportedTimeZoneId(timezone: string | null | undefined): string | null {
  const normalizedTimezone = timezone?.trim();
  if (!normalizedTimezone || !isSupportedTimeZone(normalizedTimezone)) {
    return null;
  }

  return normalizedTimezone;
}

function includeFallbackTimeZones(timeZoneIds: string[]): string[] {
  const timeZoneSet = new Set(timeZoneIds);
  const missingFallbackTimeZones = FALLBACK_TIMEZONES.filter(
    (timezone) => !timeZoneSet.has(timezone),
  );
  return [...timeZoneIds, ...missingFallbackTimeZones];
}

function getBrowserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

function getTimezoneSortLabel(timezone: string, locale?: string): string {
  return getTimezoneLabel(timezone, locale).replace(/\s*\([^)]*\)$/, "");
}

function getTimeZoneCityLabel(timezone: string): string {
  const citySegment = timezone.split("/").at(-1) ?? timezone;
  return citySegment.replaceAll("_", " ");
}

function getUtcOffsetLabel(timezone: string): string | null {
  try {
    const offsetMinutes = Math.round(getTimeZoneOffsetMs(timezone, Date.now()) / (60 * 1_000));
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absoluteOffsetMinutes = Math.abs(offsetMinutes);
    const hours = Math.floor(absoluteOffsetMinutes / 60);
    const minutes = absoluteOffsetMinutes % 60;

    return `UTC${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  } catch {
    return null;
  }
}

function getGenericTimeZoneName(timezone: string, locale?: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      hour: "numeric",
      timeZoneName: "longGeneric",
    }).formatToParts(STABLE_TIME_ZONE_LABEL_DATE);

    return parts.find((part) => part.type === "timeZoneName")?.value ?? null;
  } catch {
    return null;
  }
}

export function sanitizeBudgetInput(value: string): string {
  return value.replace(/[^\d.-]/g, "");
}
